/**
 * Types + constants for the `.TTM` execution core (ttm-interpreter.ts's
 * module doc has the timing/execution-model rationale). Split out purely to
 * keep ttm-interpreter.ts under this repo's file-size budget — nothing here
 * has any interpreter logic.
 */

import type { TtmArgs, TtmPoint, TtmScript } from "./ttm.ts";

/** 60 Hz frame duration, dgds's `MS_PER_FRAME` (`engines/dgds/dgds.h`) — see ttm-interpreter.ts's Timing section. */
export const MS_PER_FRAME = 16.6667;

/** VGA mode 13h screen dimensions — dgds's `SCREEN_WIDTH`/`SCREEN_HEIGHT`, and Red Baron's own `.SCR` default (scr.ts). */
export const TTM_SCREEN_WIDTH = 320;
export const TTM_SCREEN_HEIGHT = 200;

/** A pixel-dimensioned rectangle in screen space — always a standard `{x,y,width,height}` box, never dgds's inclusive-max wire form (see `0x4000` in ttm-interpreter-ops.ts). */
export interface TtmRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** The default draw window: the whole 320x200 screen (dgds's `TTMSeq::reset()`). */
export const FULL_SCREEN_CLIP: TtmRect = { x: 0, y: 0, width: TTM_SCREEN_WIDTH, height: TTM_SCREEN_HEIGHT };

export type TtmSpriteFlip = "v" | "h" | "hv";

/** `0xB600 COPY BUFFER`'s buffer ids (dgds's own comment: `0` background, `1` stored area, `2` composition/front). */
export type TtmBufferId = 0 | 1 | 2;

/** Pixel dimensions of a resolved asset (or one sprite-sheet frame) — the only thing `TtmEnv` exposes. */
export interface TtmAssetMeta {
  readonly width: number;
  readonly height: number;
}

/**
 * Resolves loaded asset ids to *metadata only* — the interpreter never
 * decodes or touches pixel data itself (that stays in bmp.ts/scr.ts,
 * consumed by whatever renders the command stream). Both methods return
 * `undefined` for an unresolvable id/frame rather than throwing; a command
 * whose asset didn't resolve is still emitted (`size`/`id` fields just come
 * back `undefined`), matching the disassembler's own "never throw" policy.
 */
export interface TtmEnv {
  /** Pixel dimensions of a `LOAD SCR` background, by filename (case-insensitive). */
  resolveScreen(id: string): TtmAssetMeta | undefined;
  /** Pixel dimensions of one frame of a `LOAD BMP` sprite sheet, by filename + frame index. */
  resolveSpriteFrame(id: string, frame: number): TtmAssetMeta | undefined;
}

/** The trivial `TtmEnv`: resolves nothing. Fine for structural tests that don't care about asset dimensions. */
export const NULL_TTM_ENV: TtmEnv = {
  resolveScreen: () => undefined,
  resolveSpriteFrame: () => undefined,
};

/**
 * One declarative effect produced while executing a tick's instructions.
 * Every variant carries only the values the bytecode itself gave (plus
 * resolved slot state where the wire form doesn't repeat it, e.g.
 * `drawSprite`'s `sheet`/`id`) — no pixel math, no color-table lookups, no
 * clipping performed. Coordinates for the rect-shaped ops (`drawFilledRect`,
 * wipes, `storeArea`, …) are `x,y,w,h` as dgds's `Common::Rect(Point, w, h)`
 * constructor reads them — the one exception is `setClipWindow`, whose wire
 * args are `x,y,xMax,yMax` (dgds's own comment: "NOTE: params are xmax/ymax,
 * NOT w/h") and is normalized into a standard `TtmRect` before being stored
 * as `clip` on every draw command below.
 */
export type TtmCommand =
  | { readonly kind: "loadScreen"; readonly id: string; readonly size: TtmAssetMeta | undefined }
  | { readonly kind: "loadSpriteSheet"; readonly slot: number; readonly id: string }
  | { readonly kind: "loadPalette"; readonly slot: number; readonly id: string }
  | { readonly kind: "loadSong"; readonly slot: number; readonly id: string }
  /** `0xF070 LOAD SND/SX` — Red Baron-only (docs/formats/ttm-animation.md). */
  | { readonly kind: "loadSoundBank"; readonly slot: number; readonly id: string }
  /** `0x1060 SELECT PAL` — the only `SELECT *` op with an immediate effect in dgds (`selectPalNum` fires right away, not deferred to a later load). */
  | { readonly kind: "setPalette"; readonly slot: number }
  | { readonly kind: "stopMusic" }
  /** `0x0230 FADE CURRENT MUSIC` — REPORTED shape by analogy to `0x0220 STOP CURRENT MUSIC` (0 args); unimplemented in dgds too, see ttm-interpreter.ts's module doc. */
  | { readonly kind: "fadeMusic" }
  | { readonly kind: "fadeOut"; readonly colorStart: number; readonly colorCount: number; readonly targetColor: number; readonly speed: number }
  | { readonly kind: "fadeIn"; readonly colorStart: number; readonly colorCount: number; readonly targetColor: number; readonly speed: number }
  /** `0x10C0 SELECT SND SLOT` — Red Baron-only; surfaced explicitly (unlike the silent `SELECT SONG`/`SELECT BMP` state setters) so a player can observe bank-switch ordering without also needing every `LOAD SND/SX`. */
  | { readonly kind: "selectSoundSlot"; readonly slot: number }
  /** `0x1300 PLAY SFX` — `id` indexes into whichever sound bank (`SIMSNDS.SNG`/`SHLSNDS2.SNG`-shaped `.SNG`) is currently selected; this interpreter doesn't resolve that, the player does. */
  | { readonly kind: "playSound"; readonly id: number }
  | { readonly kind: "stopSound"; readonly id: number }
  /** `0x1020 SET DELAY` — see ttm-interpreter.ts's Timing section for the unit/persistence model. */
  | { readonly kind: "delay"; readonly frames: number; readonly ms: number }
  | { readonly kind: "setClipWindow"; readonly clip: TtmRect }
  | { readonly kind: "drawPixel"; readonly x: number; readonly y: number; readonly color: number; readonly clip: TtmRect }
  | { readonly kind: "drawLine"; readonly x1: number; readonly y1: number; readonly x2: number; readonly y2: number; readonly color: number; readonly clip: TtmRect }
  /**
   * `0xA100 DRAW FILLED RECT`. dgds's switch **falls through** from this
   * case into `0xA110 DRAW EMPTY RECT`'s border draw (`ttm.cpp`, no
   * `break` between them) — so a real "filled rect" is actually two
   * strokes: the interior filled with `bg`, inset by 1px on every side,
   * then a `fg`-colored border at the *un-inset* `x,y,w,h`. This is the
   * dialect's stand-in for a dedicated "clear rect" primitive (Red Baron
   * has none): a consumer clearing a region draws this with `fg === bg`.
   */
  | { readonly kind: "drawFilledRect"; readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly fg: number; readonly bg: number; readonly clip: TtmRect }
  | { readonly kind: "drawEmptyRect"; readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly color: number; readonly clip: TtmRect }
  /** `x,y,w,h` bounding box; center/radii are `(x + w/2, y + h/2, w/2, h/2)` with C-style truncating division (dgds's `xr = ivals[2] / 2`) — left to the consumer, not precomputed here. */
  | { readonly kind: "drawFilledCircle"; readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly fg: number; readonly bg: number; readonly clip: TtmRect }
  | { readonly kind: "drawEmptyCircle"; readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly color: number; readonly clip: TtmRect }
  /**
   * `0xA500`/`0xA510`/`0xA520`/`0xA530`. `sheet`/`frame` resolve dgds's
   * "default to current brush/bmp slot" 2-arg form (the only form Red
   * Baron's corpus ever uses — docs/formats/ttm-animation.md's census) as
   * well as the explicit 4-/6-arg forms. `id` is the sprite sheet's
   * filename if this run has seen a `LOAD BMP` into `sheet`'s slot yet
   * (mirrors `ttm.test.ts`'s draw-sprite plausibility check).
   */
  | {
      readonly kind: "drawSprite";
      readonly sheet: number;
      readonly id: string | undefined;
      readonly frame: number;
      readonly x: number;
      readonly y: number;
      readonly flip: TtmSpriteFlip | undefined;
      readonly dstSize: { readonly width: number; readonly height: number } | undefined;
      readonly size: TtmAssetMeta | undefined;
      readonly clip: TtmRect;
    }
  /** `0xAF20 DRAW FILLED POLY` — dgds's `drawPolygonScan` uses only `seq._drawColFG`; `bg` plays no part (unlike `drawFilledRect`). */
  | { readonly kind: "drawFilledPoly"; readonly points: readonly TtmPoint[]; readonly color: number; readonly clip: TtmRect }
  | { readonly kind: "drawEmptyPoly"; readonly points: readonly TtmPoint[]; readonly color: number; readonly clip: TtmRect }
  /** The `0xA010`-`0xA090` wipe-transition family, unified — `style` is the opcode's name (e.g. `"WIPE LEFT-TO-RIGHT"`) since only `0xA020` (`"WIPE 20"`) is VERIFIED in the real corpus. */
  | { readonly kind: "wipe"; readonly style: string; readonly x: number; readonly y: number; readonly w: number; readonly h: number }
  | { readonly kind: "saveBackground" }
  | { readonly kind: "storeArea"; readonly x: number; readonly y: number; readonly w: number; readonly h: number }
  | { readonly kind: "saveGetPutRegion"; readonly slot: number; readonly x: number; readonly y: number; readonly w: number; readonly h: number }
  | { readonly kind: "drawGetPut"; readonly slot: number }
  | { readonly kind: "copyBuffer"; readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly srcBuffer: TtmBufferId; readonly dstBuffer: TtmBufferId }
  | { readonly kind: "pageFlip"; readonly enabled: boolean }
  /** A structurally-valid instruction this interpreter has no behaviour for — nameless (disassembler's own `kind: "unknown"`) or REPORTED-only/unimplemented-in-dgds (`0x2010 SET FRAME`, see module doc). Never thrown, always surfaced. */
  | { readonly kind: "unknown"; readonly op: number; readonly name: string | undefined; readonly args: TtmArgs };

/**
 * Immutable snapshot of interpreter state between ticks. Treat as opaque
 * except for `done`/`frameIndex`, which are safe (and meant) to inspect —
 * everything else exists purely to be threaded back into `advance()`.
 */
export interface TtmRunState {
  readonly script: TtmScript;
  readonly env: TtmEnv;
  /** Index into `script.instructions` of the next instruction `advance()` will read. */
  readonly pc: number;
  /** Number of ticks produced so far (0 before the first `advance()` call). */
  readonly frameIndex: number;
  /** `true` once `pc` has reached the end of `script.instructions` — no more ticks remain. */
  readonly done: boolean;
  readonly delayFrames: number;
  readonly brush: number;
  readonly bmpSlot: number;
  readonly palSlot: number;
  readonly songSlot: number;
  readonly sndSlot: number;
  readonly getPutSlot: number;
  readonly fg: number;
  readonly bg: number;
  readonly clip: TtmRect;
  /** `bmpSlot -> filename`, built up as `LOAD BMP` executes — how `drawSprite` resolves `id`. */
  readonly bmpSlotAsset: ReadonlyMap<number, string>;
}

export interface TtmTickResult {
  /** The updated run state — feed this back into the next `advance()` call. */
  readonly run: TtmRunState;
  /** Every command produced between the previous tick boundary and this one's `0x0FF0 FINISH FRAME` (or end of stream). */
  readonly commands: readonly TtmCommand[];
  /** `delayFrames * MS_PER_FRAME` — the hold duration in effect as of this tick, per ttm-interpreter.ts's persistence model. */
  readonly delayMs: number;
  readonly delayFrames: number;
  /** 0-based index of *this* tick (matches `run.frameIndex` before the call). */
  readonly frameIndex: number;
  /** `true` if this was the final tick — no more instructions remain. */
  readonly done: boolean;
}
